// A standalone column-header row for tables that run their DataTable with
// showHeader={false} and pin the header inside a sticky page toolbar — the
// scope-overview / trace-log pattern, where search + filters + header stick
// together and the rows scroll under them. Mirrors DataTable's own header
// markup and sizing so header and rows line up and read identically; sortable
// when onSortChange is supplied. Shared so panels don't each re-roll it.
//
// `minWidth` and the hasFlex/max-content sizing mirror DataTable's innerStyle so
// the header and the (separately mounted) rows resolve to the same width and
// stay column-aligned, including when a fixed-column table scrolls horizontally
// (both move with the page; the toolbar only pins vertically).
//
// `resizable` opts in to the per-column drag handle DataTable's own header
// carries (skipped on flex columns, which have no pixel width to drag). Off by
// default so existing consumers (the memory store) are unchanged; the inventory
// lens turns it on to keep the admin Endpoints page's resize affordance.
import ColumnResizeHandle from "./ColumnResizeHandle.jsx";

export default function ColumnHeaderRow({ cols, sortBy = null, onSortChange = null, minWidth = "100%", resizable = false }) {
  const { effective, gridCols, widths } = cols;
  const mw = typeof minWidth === "number" ? `${minWidth}px` : minWidth;
  const hasFlex = effective.some((c) => c.flex);
  const innerStyle = hasFlex ? { minWidth: mw } : { minWidth: mw, width: "max-content" };
  return (
    <div style={innerStyle}>
      <div
        className="grid items-center gap-x-2 border-b border-[color:var(--line-soft)] px-[6px] py-[4px] text-[9px] leading-[1.08] tracking-[0.08em] text-[color:var(--muted)] uppercase"
        style={{ gridTemplateColumns: gridCols }}
      >
        {effective.map((c) => {
          const active = sortBy?.id === c.id;
          return (
            <span key={c.id} className="relative truncate">
              {onSortChange && c.sortable !== false ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => onSortChange(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSortChange(c.id);
                    }
                  }}
                  className="inline-flex max-w-full cursor-pointer select-none items-center gap-[3px] align-middle transition-colors hover:text-[color:var(--ink)]"
                >
                  <span className="truncate">{c.label}</span>
                  {/* Caret reserves its slot so toggling sort doesn't reflow. */}
                  <span aria-hidden className={active ? "text-[color:var(--ink)]" : "opacity-0"}>
                    {active && sortBy.dir === "asc" ? "↑" : "↓"}
                  </span>
                </span>
              ) : (
                c.label
              )}
              {resizable && !c.flex && widths ? (
                <ColumnResizeHandle
                  currentWidth={c.width}
                  onResize={(w) => widths.setWidth(c.id, w)}
                />
              ) : null}
            </span>
          );
        })}
      </div>
    </div>
  );
}
